import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import type { Params } from 'utils/miscUtils/articlePageHelpers';
import type { PreviewData } from 'interfaces/PreviewData';
import { getBrandedArticleBySlug } from 'api/BrandedArticle/getBrandedArticleBySlug';
import BrandedArticlePage from 'pages/BrandedArticle/BrandedArticle';
import setArticleEmbeds from 'utils/miscUtils/setArticleEmbeds';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import setPageBypassPaywall from 'services/Paywall/setPageBypassPaywall';
import getFooter from 'api/Footer/getFooter';
import getMenu from 'api/Header/getMenu';
import type { HeaderNav } from 'interfaces/navigation/Header';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import { setHeadData } from 'utils/metaDataUtils/headData';
import type { BypassPaywall } from 'interfaces/BypassPaywall';
import { setPianoConfig } from 'services/Piano';
import setBrandedArticlePageAdConfig from 'services/Ad/config/setBrandedArticlePageAdConfig';
import type { BrandedArticle } from 'interfaces/content/articles/Post';
import { clearHTMLString } from 'utils/stringUtils';

interface BrandedArticleProps {
  brandedArticle: BrandedArticle;
}

const BrandedArticleNextPage: NextPage<BrandedArticleProps> = ({ brandedArticle }: BrandedArticleProps) => {
  const hideInstreamAds = brandedArticle.adTags?.find((tag) => tag.slug === 'hide-instream-ads');
  const adFrequency = hideInstreamAds ? 0 : 7;

  return (
    <BrandedArticlePage
      brandedArticle={brandedArticle}
      lastPWithoutAds={1}
      adFrequency={adFrequency}
      embeds={setArticleEmbeds(brandedArticle)}
      hideInstreamAds={Boolean(hideInstreamAds)}
    />
  );
};

export default BrandedArticleNextPage;

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { slug } = context.params as Params;
    const brandedArticle =
      context.preview && context.previewData
        ? await getBrandedArticleBySlug('', context.previewData as PreviewData)
        : await getBrandedArticleBySlug(slug);

    if (!brandedArticle.description) {
      brandedArticle.description =
        `${brandedArticle.title} ${clearHTMLString(brandedArticle.content).replace(/\n/g, ' ')}`.substring(0, 160);
    }

    if (brandedArticle.socialTitle) {
      brandedArticle.socialTitle = brandedArticle.title;
    }

    if (!brandedArticle.socialDescription) {
      brandedArticle.socialDescription = brandedArticle.description;
    }

    const menu: HeaderNav = await getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null);
    const footer = await getFooter();
    const dataLayer = setPageGtmConfig(brandedArticle);
    const headData = setHeadData(brandedArticle);
    const bypassPaywall: BypassPaywall = setPageBypassPaywall(brandedArticle);
    const arcSpanPageConfig = setarcSpanPageConfig(brandedArticle);
    const pianoPageConfig = setPianoConfig(brandedArticle);
    const pageAdConfig = setBrandedArticlePageAdConfig(brandedArticle);

    return {
      props: {
        arcSpanPageConfig,
        brandedArticle,
        bypassPaywall,
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        hideHeaderFooter: brandedArticle.hideHeaderFooter === '1',
        pageAdConfig,
        pianoPageConfig,
        isBranded: true,
      },
      revalidate: 60, // In seconds
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: {
            partner: 'test',
            slug: '/testing-the-branded-article', // this to be changed
          },
        },
      ],
    };
  }

  return {
    fallback: 'blocking',
    paths: [
      {
        params: {
          partner: 'test',
          slug: '/test/testing-the-branded-article',
        },
      },
    ],
  };
};
